import rawConfig from '../data/config.json';
import rawPatients from '../data/patients.json';
import rawRooms from '../data/rooms.json';
import rawRoomVisuals from '../data/room-visuals.json';
import { nextRandomFloat, nextRandomInt, normalizeSeed } from './prng';
import { FIXED_TICK_MS } from './time';
import type {
  CommandResult,
  Patient,
  PatientDefinition,
  PlacedRoom,
  RoomDefinition,
  RoomVisualDefinition,
  SimCommand,
  SimState,
  Staff
} from './types';

const ROOM_REGISTRY: ReadonlyMap<string, RoomDefinition> = new Map(
  (rawRooms as RoomDefinition[]).map((room) => [room.id, { ...room }])
);

const ROOM_VISUALS_REGISTRY: ReadonlyMap<string, RoomVisualDefinition> = new Map(
  (rawRoomVisuals as RoomVisualDefinition[]).map((v) => [v.id, { ...v }])
);

const PATIENT_REGISTRY: ReadonlyMap<string, PatientDefinition> = new Map(
  (rawPatients as PatientDefinition[]).map((patient) => [patient.typeId, { ...patient }])
);

const VALID_SPEEDS: ReadonlySet<number> = new Set(rawConfig.time.validSpeeds);

export function getRoomDefinitions(): RoomDefinition[] {
  return (rawRooms as RoomDefinition[]).map((room) => ({ ...room }));
}

export function getRoomDefinition(typeId: string): RoomDefinition | undefined {
  return ROOM_REGISTRY.get(typeId);
}

export function getRoomVisualDefinitions(): RoomVisualDefinition[] {
  return (rawRoomVisuals as RoomVisualDefinition[]).map((v) => ({ ...v }));
}

export function getRoomVisualDefinition(typeId: string): RoomVisualDefinition | undefined {
  return ROOM_VISUALS_REGISTRY.get(typeId);
}

export function getPatientDefinitions(): PatientDefinition[] {
  return (rawPatients as PatientDefinition[]).map((patient) => ({ ...patient }));
}

export function getPatientDefinition(typeId: string): PatientDefinition | undefined {
  return PATIENT_REGISTRY.get(typeId);
}

export function createInitialState(seed: number = rawConfig.defaultSeed): SimState {
  const normalized = normalizeSeed(seed);
  return {
    rngSeed: normalized,
    rngState: normalized,
    nextRoomId: 1,
    nextPatientId: 1,
    nextStaffId: 1,
    money: rawConfig.startingMoney,
    reputation: rawConfig.reputation.initial,
    totalGameMs: 0,
    isPaused: false,
    speedMultiplier: 1,
    rooms: [],
    patients: [],
    staff: [],
    spawnTimerMs: 0,
    gridConfig: {
      cols: rawConfig.grid.cols,
      floors: rawConfig.grid.floors,
      stairCol: rawConfig.grid.stairCol,
      entranceCol: rawConfig.grid.entranceCol
    }
  };
}

export class Simulation {
  private state: SimState;

  constructor(seed: number = rawConfig.defaultSeed, initialState?: SimState) {
    if (initialState) {
      this.state = JSON.parse(JSON.stringify(initialState)) as SimState;
    } else {
      this.state = createInitialState(seed);
    }
  }

  public getState(): Readonly<SimState> {
    return this.state;
  }

  public getSnapshot(): SimState {
    return JSON.parse(JSON.stringify(this.state)) as SimState;
  }

  public loadState(newState: SimState): void {
    this.state = JSON.parse(JSON.stringify(newState)) as SimState;
    this.state.isPaused = true; // Selalu jeda saat dimuat
  }

  public serialize(): string {
    return JSON.stringify(this.state);
  }

  public static deserialize(serializedJson: string): Simulation {
    const parsed = JSON.parse(serializedJson) as SimState;
    return new Simulation(parsed.rngSeed, parsed);
  }

  public nextRandom(): number {
    return nextRandomFloat(this.state);
  }

  public tick(dtMs: number = FIXED_TICK_MS): void {
    if (dtMs !== FIXED_TICK_MS) {
      throw new Error(`Simulation.tick expects fixed timestep of ${FIXED_TICK_MS}ms, received ${dtMs}ms`);
    }
    if (this.state.isPaused) {
      return;
    }

    this.state.totalGameMs += FIXED_TICK_MS;

    // 1. Spawning Pasien Baru
    this.updateSpawning(dtMs);

    // 2. Pembaruan Logika Pasien
    this.updatePatients(dtMs);

    // 3. Pembaruan Logika Staf
    this.updateStaff(dtMs);

    // 4. Cleanup Pasien
    this.cleanupPatients();
  }

  public advanceFrame(
    rawDeltaMs: number,
    currentAccumulator: number
  ): { accumulator: number; ticksExecuted: number } {
    if (this.state.isPaused) {
      return { accumulator: 0, ticksExecuted: 0 };
    }

    const clampedDelta = Math.min(
      Math.max(0, rawDeltaMs),
      rawConfig.time.maxDeltaClampMs
    );
    let accumulator = currentAccumulator + clampedDelta * this.state.speedMultiplier;

    let ticksExecuted = 0;
    const maxTicks = rawConfig.time.maxTicksPerFrame;

    while (accumulator >= FIXED_TICK_MS && ticksExecuted < maxTicks) {
      this.tick(FIXED_TICK_MS);
      accumulator -= FIXED_TICK_MS;
      ticksExecuted++;
    }

    if (ticksExecuted >= maxTicks && accumulator >= FIXED_TICK_MS) {
      accumulator = 0;
    }

    return { accumulator, ticksExecuted };
  }

  public getRoomAt(floor: number, col: number): PlacedRoom | undefined {
    for (const room of this.state.rooms) {
      if (
        room.floor === floor &&
        col >= room.startCol &&
        col < room.startCol + room.width
      ) {
        return room;
      }
    }
    return undefined;
  }

  public hasStructuralSupport(floor: number, col: number): boolean {
    const { stairCol, entranceCol } = this.state.gridConfig;

    if (col === stairCol) {
      return false;
    }
    if (floor === 0) {
      return true;
    }

    const floorBelow = floor - 1;
    if (floorBelow === 0 && col === entranceCol) {
      return true;
    }

    return this.getRoomAt(floorBelow, col) !== undefined;
  }

  public canBuild(typeId: string, floor: number, col: number): CommandResult {
    const roomDef = ROOM_REGISTRY.get(typeId);
    if (!roomDef) {
      return { ok: false, reason: 'UNKNOWN_ROOM_TYPE' };
    }

    if (
      !Number.isInteger(floor) ||
      !Number.isInteger(col) ||
      floor < 0 ||
      col < 0
    ) {
      return { ok: false, reason: 'INVALID_COORDINATES' };
    }

    const { cols, floors, stairCol, entranceCol } = this.state.gridConfig;
    const endColExclusive = col + roomDef.width;

    if (floor >= floors || endColExclusive > cols) {
      return { ok: false, reason: 'OUT_OF_BOUNDS' };
    }

    for (let c = col; c < endColExclusive; c++) {
      if (floor === 0 && c === entranceCol) {
        return { ok: false, reason: 'RESERVED_ENTRANCE' };
      }
    }

    for (let c = col; c < endColExclusive; c++) {
      if (c === stairCol) {
        return { ok: false, reason: 'RESERVED_STAIRWELL' };
      }
    }

    for (let c = col; c < endColExclusive; c++) {
      if (this.getRoomAt(floor, c) !== undefined) {
        return { ok: false, reason: 'OVERLAPPING' };
      }
    }

    if (floor > 0) {
      for (let c = col; c < endColExclusive; c++) {
        if (!this.hasStructuralSupport(floor, c)) {
          return { ok: false, reason: 'NO_STRUCTURAL_SUPPORT' };
        }
      }
    }

    if (this.state.money < roomDef.cost) {
      return { ok: false, reason: 'INSUFFICIENT_FUNDS' };
    }

    return { ok: true };
  }

  public dispatch(command: SimCommand): CommandResult {
    switch (command.type) {
      case 'TOGGLE_PAUSE': {
        this.state.isPaused = !this.state.isPaused;
        return { ok: true };
      }

      case 'SET_SPEED': {
        if (!Number.isInteger(command.speed) || !VALID_SPEEDS.has(command.speed)) {
          return { ok: false, reason: 'INVALID_SPEED' };
        }
        this.state.speedMultiplier = command.speed;
        return { ok: true };
      }

      case 'BUILD_ROOM': {
        const check = this.canBuild(command.typeId, command.floor, command.col);
        if (!check.ok) {
          return check;
        }

        const roomDef = ROOM_REGISTRY.get(command.typeId)!;
        const newRoom: PlacedRoom = {
          id: this.state.nextRoomId++,
          typeId: roomDef.id,
          floor: command.floor,
          startCol: command.col,
          width: roomDef.width
        };

        this.state.money -= roomDef.cost;
        this.state.rooms.push(newRoom);

        // Rekrut Staf otomatis jika diperlukan
        if (newRoom.typeId === 'registration') {
          this.state.staff.push({
            id: this.state.nextStaffId++,
            role: 'receptionist',
            assignedRoomId: newRoom.id,
            floor: newRoom.floor,
            col: newRoom.startCol,
            isBusy: false,
            currentPatientId: null,
            busyRemainingMs: 0
          });
        } else if (newRoom.typeId === 'general_clinic') {
          this.state.staff.push({
            id: this.state.nextStaffId++,
            role: 'doctor',
            assignedRoomId: newRoom.id,
            floor: newRoom.floor,
            col: newRoom.startCol + 1,
            isBusy: false,
            currentPatientId: null,
            busyRemainingMs: 0
          });
        }

        return { ok: true, room: { ...newRoom } };
      }
    }
  }

  private updateSpawning(dtMs: number): void {
    const hasRegistration = this.state.rooms.some((r) => r.typeId === 'registration');
    if (!hasRegistration) {
      return;
    }

    this.state.spawnTimerMs += dtMs;

    const repFactor = Math.max(0.1, this.state.reputation / 100);
    const intervalMs = Math.max(
      rawConfig.spawning.minIntervalMs,
      Math.round(rawConfig.spawning.baseIntervalMs / repFactor)
    );

    if (this.state.spawnTimerMs >= intervalMs) {
      this.state.spawnTimerMs = 0;
      this.spawnPatient();
    }
  }

  private spawnPatient(): void {
    const defs = getPatientDefinitions();
    const totalWeight = defs.reduce((acc, d) => acc + d.spawnWeight, 0);
    const roll = nextRandomInt(this.state, 1, totalWeight);

    let cumulative = 0;
    let selectedDef = defs[0];
    for (const d of defs) {
      cumulative += d.spawnWeight;
      if (roll <= cumulative) {
        selectedDef = d;
        break;
      }
    }

    const regRoom = this.findAvailableRegistrationRoom();
    const targetFloor = regRoom ? regRoom.floor : 0;
    const targetCol = regRoom ? regRoom.startCol : 1;

    const patient: Patient = {
      id: this.state.nextPatientId++,
      typeId: selectedDef.typeId,
      state: 'WALKING_TO_REG',
      floor: 0,
      col: 0,
      targetFloor,
      targetCol,
      patienceRemainingMs: selectedDef.patienceMs,
      totalPatienceMs: selectedDef.patienceMs,
      assignedRoomId: regRoom ? regRoom.id : null
    };

    this.state.patients.push(patient);
  }

  private findAvailableRegistrationRoom(): PlacedRoom | undefined {
    return this.state.rooms.find((r) => r.typeId === 'registration');
  }

  private findAvailableWaitingRoom(): PlacedRoom | undefined {
    return this.state.rooms.find((r) => r.typeId === 'waiting_room');
  }

  private stepPathfinding(patient: Patient): boolean {
    const { stairCol } = this.state.gridConfig;

    if (patient.floor === patient.targetFloor) {
      if (patient.col < patient.targetCol) {
        patient.col += 1;
        return false;
      } else if (patient.col > patient.targetCol) {
        patient.col -= 1;
        return false;
      }
      return true;
    }

    if (patient.col < stairCol) {
      patient.col += 1;
      return false;
    } else if (patient.col > stairCol) {
      patient.col -= 1;
      return false;
    }

    if (patient.floor < patient.targetFloor) {
      patient.floor += 1;
    } else if (patient.floor > patient.targetFloor) {
      patient.floor -= 1;
    }

    return false;
  }

  private updatePatients(dtMs: number): void {
    for (const patient of this.state.patients) {
      if (
        patient.state === 'QUEUEING_REG' ||
        patient.state === 'WALKING_TO_REG' ||
        patient.state === 'WAITING' ||
        patient.state === 'WALKING_TO_WAITING'
      ) {
        patient.patienceRemainingMs -= dtMs;
        if (patient.patienceRemainingMs <= 0) {
          const assignedStaff = this.state.staff.find((s) => s.currentPatientId === patient.id);
          if (assignedStaff) {
            assignedStaff.isBusy = false;
            assignedStaff.currentPatientId = null;
          }

          patient.state = 'LEAVING_RAGE';
          patient.targetFloor = 0;
          patient.targetCol = 0;
          this.state.reputation = Math.max(
            rawConfig.reputation.min,
            this.state.reputation - rawConfig.reputation.lossPerRage
          );
          continue;
        }
      }

      switch (patient.state) {
        case 'WALKING_TO_REG': {
          const reached = this.stepPathfinding(patient);
          if (reached) {
            patient.state = 'QUEUEING_REG';
          }
          break;
        }

        case 'WALKING_TO_WAITING': {
          const reached = this.stepPathfinding(patient);
          if (reached) {
            patient.state = 'WAITING';
          }
          break;
        }

        case 'WALKING_TO_CLINIC': {
          const reached = this.stepPathfinding(patient);
          if (reached) {
            patient.state = 'BEING_TREATED';
          }
          break;
        }

        case 'LEAVING_TREATED':
        case 'LEAVING_RAGE': {
          this.stepPathfinding(patient);
          break;
        }
      }
    }
  }

  private updateStaff(dtMs: number): void {
    for (const staff of this.state.staff) {
      if (staff.role === 'receptionist') {
        this.updateReceptionist(staff, dtMs);
      } else if (staff.role === 'doctor') {
        this.updateDoctor(staff, dtMs);
      }
    }
  }

  private updateReceptionist(staff: Staff, dtMs: number): void {
    if (staff.isBusy) {
      staff.busyRemainingMs -= dtMs;
      if (staff.busyRemainingMs <= 0) {
        const patient = this.state.patients.find((p) => p.id === staff.currentPatientId);
        if (patient && patient.state === 'REGISTERING') {
          const waitingRoom = this.findAvailableWaitingRoom();
          if (waitingRoom) {
            patient.targetFloor = waitingRoom.floor;
            patient.targetCol = waitingRoom.startCol;
            patient.state = 'WALKING_TO_WAITING';
          } else {
            patient.targetFloor = 0;
            const maxCol = this.state.gridConfig.cols - 1;
            patient.targetCol = Math.min(maxCol, staff.col + 2);
            patient.state = 'WALKING_TO_WAITING';
          }
        }
        staff.isBusy = false;
        staff.currentPatientId = null;
      }
      return;
    }

    const queuedPatient = this.state.patients.find(
      (p) => p.state === 'QUEUEING_REG' && p.floor === staff.floor && p.col === staff.col
    );

    if (queuedPatient) {
      staff.isBusy = true;
      staff.currentPatientId = queuedPatient.id;
      staff.busyRemainingMs = rawConfig.staff.receptionistDurationMs;
      queuedPatient.state = 'REGISTERING';
    }
  }

  private updateDoctor(staff: Staff, dtMs: number): void {
    if (staff.isBusy) {
      staff.busyRemainingMs -= dtMs;
      if (staff.busyRemainingMs <= 0) {
        const patient = this.state.patients.find((p) => p.id === staff.currentPatientId);
        if (patient && (patient.state === 'BEING_TREATED' || patient.state === 'WALKING_TO_CLINIC')) {
          const patientDef = getPatientDefinition(patient.typeId);
          if (patientDef) {
            this.state.money += patientDef.fee;
          }

          this.state.reputation = Math.min(
            rawConfig.reputation.max,
            this.state.reputation + rawConfig.reputation.gainPerTreated
          );

          patient.state = 'LEAVING_TREATED';
          patient.targetFloor = 0;
          patient.targetCol = 0;
        }

        staff.isBusy = false;
        staff.currentPatientId = null;
      }
      return;
    }

    const waitingPatient = this.state.patients.find((p) => p.state === 'WAITING');
    if (waitingPatient) {
      const clinicRoom = this.state.rooms.find((r) => r.id === staff.assignedRoomId);
      if (clinicRoom) {
        staff.isBusy = true;
        staff.currentPatientId = waitingPatient.id;

        const patientDef = getPatientDefinition(waitingPatient.typeId);
        staff.busyRemainingMs = patientDef ? patientDef.serviceDurationMs : rawConfig.staff.defaultDoctorDurationMs;

        waitingPatient.targetFloor = clinicRoom.floor;
        waitingPatient.targetCol = clinicRoom.startCol + 1;
        waitingPatient.state = 'WALKING_TO_CLINIC';
      }
    }
  }

  private cleanupPatients(): void {
    this.state.patients = this.state.patients.filter((p) => {
      const reachedExit =
        (p.state === 'LEAVING_TREATED' || p.state === 'LEAVING_RAGE') &&
        p.floor === 0 &&
        p.col === 0;
      return !reachedExit;
    });
  }
}
